// ApiSearchDropdown.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppContext } from "../../../../../context/AppContext";
import { API_ENDPOINTS } from "../../../../../utils/constants";

const ApiSearchDropdown = ({ field, onChange,onOptionsUpdate = () => {}, hasUnmatchedInput = '' }) => {
    const { sharedApiService, selectedLanguageData } = useAppContext();
    const apiRequest = sharedApiService.sendRequest;

    const dropdownRef = useRef(null);
    const portalRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedText, setSelectedText] = useState("");

    const MIN_SEARCH_LENGTH = 2;
    const SEARCH_DELAY = 300;
    const debounceTimer = useRef(null);
    const safeOptions = options;//Array.isArray(field?.options) ? field.options : [];


    /** -----------------------------
     * Fetch options from API
     ------------------------------*/
    const fetchOptions = async (query) => {
        if (!query || query.length < MIN_SEARCH_LENGTH) return;

        setLoading(true);

        try {
            await apiRequest({
                method: "GET",
                url: `${API_ENDPOINTS.searchAdditionalFields}?language=${selectedLanguageData?.code}&fieldName=${field.FieldName}&query=${encodeURIComponent(query)}`,
                abortable: true,
                onSuccess: (res) => {
                    if (res && Array.isArray(res)) {
                        const formattedResults = res.map((item, index) => {
                            if (item.Key !== undefined && item.Value !== undefined) {
                                return {
                                    key: `${item.Key ?? item.value}-${index}`,
                                    Value: item.Key,
                                    Text: item.Value,
                                    Selected: false,
                                    Disabled: false,
                                    Group: null
                                };
                            } else {
                                return {
                                    Value: item.value || item.Value || item.id || item,
                                    Text: item.text || item.Text || item.name || item,
                                    Selected: false,
                                    Disabled: false,
                                    Group: null
                                };
                            }
                        });
                        setOptions(formattedResults);
                        // onOptionsUpdate(field.FieldName, formattedResults);
                        if (typeof onOptionsUpdate === "function") {
                            onOptionsUpdate(field.FieldName, formattedResults);
                        }                          
                    }
                },
                onError: () => {
                    // setOptions([]);
                    if (typeof onOptionsUpdate === "function") {
                        onOptionsUpdate(field.FieldName, []);
                    }
                }
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasUnmatchedInput) {
            setIsOpen(true);
            setSearch(hasUnmatchedInput);
            // setOptions([]);
            if (typeof onOptionsUpdate === "function") {
                onOptionsUpdate(field.FieldName, []);
            }
            fetchOptions(hasUnmatchedInput);
        }
    }, [hasUnmatchedInput]);

    /** -----------------------------
     * Debounced Search Trigger
     ------------------------------*/
    useEffect(() => {
        clearTimeout(debounceTimer.current);

        if (!isOpen) return;

        debounceTimer.current = setTimeout(() => {
            fetchOptions(search);
        }, SEARCH_DELAY);

        return () => clearTimeout(debounceTimer.current);
    }, [search, isOpen]);

    /** -----------------------------
     * Outside Click Close
     ------------------------------*/
    useEffect(() => {
        const handler = (e) => {
            const inside =
                dropdownRef.current?.contains(e.target) ||
                portalRef.current?.contains(e.target);

            if (!inside) setIsOpen(false);
        };

        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    /** -----------------------------
     * Dropdown positioning
     ------------------------------*/
    const getDropdownStyle = () => {
        if (!dropdownRef.current) return {};

        const rect = dropdownRef.current.getBoundingClientRect();
        return {
            left: rect.left,
            top: rect.bottom + 6,
            width: rect.width
        };
    };

    /** -----------------------------
     * On Option Select
     ------------------------------*/
    const handleSelect = (option) => {
        setSelectedText(option.Text);
        setIsOpen(false);
        setSearch("");
        // setOptions([]);
        if (typeof onOptionsUpdate === "function") {
            onOptionsUpdate(field.FieldName, []);
        }

        // Emit selected value to parent
        onChange(field, option.Value);
    };

    /** -----------------------------
     * UI
     ------------------------------*/
    return (
        <div className="relative w-full" ref={dropdownRef}>

            {/* Input */}
            <input
                type="text"
                placeholder={`Search ${field.DisplayLable}...`}
                value={isOpen ? search : selectedText}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setOptions([]);
                    if (!isOpen) setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                className="border rounded-md px-3 py-2 w-full"
            />

            {/* Dropdown Portal */}
            {isOpen && createPortal(
                <div
                    ref={portalRef}
                    className="fixed bg-white border rounded-md shadow-lg max-h-60 overflow-auto z-[9999]"
                    style={getDropdownStyle()}
                >
                    {loading ? (
                        <div className="flex items-center justify-center p-8">
                        <div className="relative">
                            {/* Spinning circle */}
                            <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin"></div>
                            {/* Optional: Inner pulse effect */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-blue-500 rounded-full animate-pulse opacity-20"></div>
                        </div>
                    </div>
                    ) : safeOptions.length > 0 ? (
                        safeOptions.map((opt) => (
                          <div
                            key={opt.key}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onMouseDown={() => handleSelect(opt)}
                          >
                            {opt.Text}
                          </div>
                        ))
                    ) : search.length >= MIN_SEARCH_LENGTH ? (
                        <div className="p-3 text-gray-500">No results</div>
                    ) : (
                        <div className="p-3 text-gray-400">
                            Type {MIN_SEARCH_LENGTH} letters to search...
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default ApiSearchDropdown;
